import React from 'react'

import {
  ActionLine,
  statusFilterOptions,
  SUPPORT_TYPE_VALUES,
  SupportTypeBadge,
} from 'components'
import { SUPPORT_REQUEST_TYPES } from 'constants/statuses'

import type { SupportRequest } from 'services/supportApi'

// The status-vocabulary resolution lives in ONE module, shared with the request
// detail page so the list and the detail cannot disagree about the same row.
// Read the long note there before touching either.
import { displayStatusKey, SUPPORT_DISPLAY_STATUSES } from '../displayStatus'

/**
 * Search / Filter / Sort for a request list — the SAME toolbar every other list surface uses.
 *
 * ## This replaces a hand-assembled row, and the reasoning that justified it
 *
 * The previous version put Search + a sort `Select` on one row and two rows of always-visible
 * chip filters under it, arguing that `ActionLine` was unusable here because it is "a DataGrid
 * toolbar" whose `Sort` and `Filters` children need `apiRef` and column definitions. That is
 * wrong on the load-bearing part. `apiRef` is OPTIONAL in all three: `Filters.commitFilters`
 * guards it (`if (apiRef)`) and otherwise reports the applied set through `onChange`, `Sort`
 * has a matching `onChange(field, direction, override)` fallback, and `Search` only touches
 * `apiRef` for the grid quick-filter. The columns are not a grid's columns either — they are
 * just a description of what can be filtered and sorted, which a card list has as much as a
 * table does. The proof it works was already in the repo: the tag Shop is a CARD GRID with no
 * grid and no `apiRef`, and it drives this exact toolbar (`pages/main/TagsPage/.../Shop.tsx`).
 *
 * So the list stays cards — that part of the old reasoning holds, a request has no columns
 * worth aligning — while its CONTROLS match every other list in the app.
 *
 * ## Why the facets moved into the drawer
 *
 * The old layout spent two rows of permanent vertical space on eight chips, above content
 * whose whole point is to be read. Filtering is occasional; reading the list is not. Putting
 * Status and Type behind the Filter button costs one click on the rare pass and gives the
 * rows back the space on every other one — and it is what a user arriving from Certificates
 * or Orders already knows how to do.
 *
 * Status keeps the pill treatment (it is a lifecycle state, same as every other status filter
 * in the app). Type renders as the What's-new style BADGE — and specifically as the exact
 * `SupportTypeBadge` the cards render, not a lookalike; see that component for why.
 */

/** One entry of the shared drawer's applied-filter model. */
export interface SupportFilterItem {
  field: string
  operator: string
  value: string[]
}

/** One entry of the shared `Sort` control's model — the same shape a DataGrid sort model uses. */
export interface SupportSortItem {
  field: string
  sort: 'asc' | 'desc'
}

export interface SupportListFilters {
  query: string
  /** Applied facets, as the shared Filter drawer reports them. */
  filters: SupportFilterItem[]
  /** Applied orderings, as the shared Sort control reports them. Empty = the default below. */
  sort: SupportSortItem[]
}

export const EMPTY_SUPPORT_FILTERS: SupportListFilters = {
  query: '',
  filters: [],
  sort: [],
}

/**
 * Newest first, applied whenever the user has not chosen an ordering.
 *
 * Held here rather than seeded into `sort` so the Sort button reads as un-applied until the
 * user actually deviates: a permanently-lit count badge carries no information (the shared
 * control has an `isDefault` prop for exactly this problem on server-ordered tables).
 */
const DEFAULT_SORT: SupportSortItem[] = [{ field: 'createdAt', sort: 'desc' }]

/** True when the user has NARROWED the list — decides "no matches" vs "no requests". */
export const hasActiveFilters = (f: SupportListFilters): boolean =>
  f.query.trim().length > 0 || f.filters.length > 0

/**
 * Compare two requests on one field, by what the field MEANS.
 *
 * Dates are compared as instants, not as the strings they arrive in. Everything else falls
 * back to its display LABEL — which is what makes the shared control's "A - Z" honest: it
 * sorts by the words the reader can see ("Bug", "Feature", "Support"), not by the raw enum
 * value that happens to back them.
 */
const compareOn = (a: SupportRequest, b: SupportRequest, field: string): number => {
  if (field === 'createdAt' || field === 'updatedAt') {
    return new Date(a[field]).getTime() - new Date(b[field]).getTime()
  }

  // Widened to a plain lookup deliberately: a union of the two STATUSES maps would make
  // `keyof typeof labels` their INTERSECTION, which is empty — so indexing it resolves to
  // `never` and the `.label` read does not compile.
  const labels: Record<string, { label: string }> | undefined =
    field === 'type'
      ? SUPPORT_REQUEST_TYPES
      : field === 'status'
        ? SUPPORT_DISPLAY_STATUSES
        : undefined

  const read = (r: SupportRequest) => {
    // Status sorts on what the row DISPLAYS, not on the raw column — otherwise
    // sorting by status orders promoted features by a request status the reader
    // cannot see anywhere on the card.
    const key =
      field === 'status'
        ? displayStatusKey(r)
        : String((r as unknown as Record<string, unknown>)[field] ?? '')
    return labels?.[key]?.label ?? key
  }

  return read(a).localeCompare(read(b))
}

/**
 * Apply search, facets and ordering to the rows, client-side.
 *
 * Client-side is right here and is not a shortcut: the endpoint returns one company's requests
 * (or one user's), which is tens of rows, not thousands — the whole list is already in memory
 * before this runs. Server-side filtering would add a round trip per keystroke to narrow an
 * array that is already local. If a company ever accumulates enough requests for this to
 * matter, the shape to move server-side is this function's signature, unchanged.
 */
export const applySupportFilters = (
  requests: SupportRequest[],
  f: SupportListFilters,
): SupportRequest[] => {
  const q = f.query.trim().toLowerCase()

  const filtered = requests.filter(request => {
    // The drawer emits `isAnyOf` over an array of values, one entry per facet. Every facet
    // must match (AND across fields), any value within one may (OR within a field) — the
    // standard faceted-browse reading, and the same one `Shop` applies.
    const matchesFacets = f.filters.every(filter => {
      if (!Array.isArray(filter.value) || filter.value.length === 0) return true
      // Status matches on the DISPLAYED key, so selecting "Building" selects the
      // rows whose pill says Building. Reading the raw column here would make the
      // filter disagree with the list it is filtering.
      const value =
        filter.field === 'status'
          ? displayStatusKey(request)
          : String((request as unknown as Record<string, unknown>)[filter.field] ?? '')
      return filter.value.includes(value)
    })
    if (!matchesFacets) return false
    if (!q) return true

    // Searches the body, the reference number and the submitter — the three things someone
    // actually has to hand when looking for a request. The reference is matched with and
    // without its "#", because that is how people type it.
    const haystack = [
      request.body,
      `#${request.reference}`,
      String(request.reference),
      request.submitterName ?? '',
      request.submitterEmail ?? '',
    ]
      .join(' ')
      .toLowerCase()

    return haystack.includes(q)
  })

  const model = f.sort.length > 0 ? f.sort : DEFAULT_SORT

  // Sorted on a COPY — `filter` already returned a new array, but returning a sorted view of
  // the query cache's own array would mutate cached data in place.
  return [...filtered].sort((a, b) => {
    for (const entry of model) {
      const comparison = compareOn(a, b, entry.field)
      if (comparison !== 0) return entry.sort === 'desc' ? -comparison : comparison
    }
    return 0
  })
}

/**
 * What can be filtered and sorted. NOT a grid's columns — the list is cards — but the same
 * description the shared toolbar reads, which is why it can drive a card list at all.
 *
 * Ordering is load-bearing in two places at once, because `ActionLine` hands this one array to
 * both controls: the drawer renders its filterable entries in this order, and the Sort menu
 * lists all of them in it. Dates first so the Sort menu opens on the orderings anyone actually
 * wants (newest / oldest / recently updated, which is exactly Created ± and Updated desc);
 * Type before Status so the drawer's two facets sit in the same order as the two tokens on the
 * card they filter.
 */
const COLUMNS = [
  {
    field: 'createdAt',
    headerName: 'Created',
    type: 'dateTime',
    // Sort-only. A date-range filter is not something this list was asked for, and the drawer
    // skips any column marked unfilterable — so Status and Type are the only two facets in it.
    filterable: false,
  },
  {
    field: 'updatedAt',
    headerName: 'Updated',
    type: 'dateTime',
    filterable: false,
  },
  {
    field: 'type',
    headerName: 'Type',
    type: 'statusChip',
    // `sortType` overrides the render type for sort SEMANTICS: 'statusChip' has no ordering,
    // so without this the control falls through to its unlabelled default.
    sortType: 'string',
    options: SUPPORT_TYPE_VALUES.map(value => ({
      value,
      label: SUPPORT_REQUEST_TYPES[value].label,
      color: SUPPORT_REQUEST_TYPES[value].color,
    })),
    // The facet draws itself with the SAME component the cards use (§36). Passing options
    // through the drawer's own badge styling would have produced a second badge that merely
    // resembles the first, which is the failure this avoids.
    renderOption: (option: { value: string }, isSelected: boolean) => (
      <SupportTypeBadge type={option.value} size="medium" selected={isSelected} />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    type: 'statusChip',
    sortType: 'string',
    // Pills, from the same map the card's status chip reads — so a filter chip and the chip
    // on the card it selects are the same token. That map now spans BOTH vocabularies
    // (see the block above `displayStatusKey`): five request statuses plus five
    // `board:`-prefixed board statuses, each keeping its own colours and label.
    options: statusFilterOptions(SUPPORT_DISPLAY_STATUSES),
  },
]

interface SupportListControlsProps {
  value: SupportListFilters
  onChange: (next: SupportListFilters) => void
}

export const SupportListControls = ({ value, onChange }: SupportListControlsProps) => {
  const set = (patch: Partial<SupportListFilters>) => onChange({ ...value, ...patch })

  /**
   * Translate the shared `Sort` control's callback into a sort model.
   *
   * It speaks four different shapes depending on what the user did — a whole replacement
   * array (drag-reorder), a clear-all, a single column added, a single column's direction
   * changed — because it was written against the DataGrid API and keeps that vocabulary on
   * the `onChange` fallback path. Handled the same way `Shop` handles it.
   */
  const handleSortChange = (
    column: string | SupportSortItem[],
    direction?: 'asc' | 'desc' | 'clear',
    override?: boolean,
  ) => {
    if (Array.isArray(column)) return set({ sort: column })
    if (column === 'all' && direction === 'clear') return set({ sort: [] })
    if (typeof column !== 'string' || !direction || direction === 'clear') return

    if (override) return set({ sort: [{ field: column, sort: direction }] })

    const existing = value.sort.findIndex(s => s.field === column)
    if (existing === -1) {
      return set({ sort: [...value.sort, { field: column, sort: direction }] })
    }
    const next = [...value.sort]
    next[existing] = { field: column, sort: direction }
    set({ sort: next })
  }

  return (
    <ActionLine
      // No row selection on a card list, so the control that toggles it would do nothing.
      showBulkActions={false}
      columns={COLUMNS}
      sort={value.sort}
      showSort
      showFilters
      search={value.query}
      onSearchChange={next => set({ query: next })}
      // Two-way, not just out. Without this the drawer never learns that the page reset the
      // applied set — "No requests match your filters → Clear filters" emptied `value.filters`
      // while the drawer kept its selections and the Filter button kept its count badge lit.
      filters={value.filters}
      onFiltersChange={next => set({ filters: next as SupportFilterItem[] })}
      onSortChange={handleSortChange}
    />
  )
}
